export type CsvValue = string | number | null | undefined;

export interface CsvSection {
  title: string;
  headers: string[];
  rows: CsvValue[][];
}

function escapeCsvValue(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function toCsvRow(values: CsvValue[]): string {
  return values.map(escapeCsvValue).join(',');
}

/**
 * Build a single CSV string from several labelled tables. A section with no
 * rows still contributes its title and header line, so the exported file is
 * always valid and non-empty even before a store has any data.
 */
export function buildCsv(sections: CsvSection[]): string {
  const lines: string[] = [];

  sections.forEach((section, index) => {
    if (index > 0) lines.push('');
    lines.push(toCsvRow([section.title]));
    lines.push(toCsvRow(section.headers));
    if (section.rows.length === 0) {
      lines.push(toCsvRow(['No data for this period']));
      return;
    }
    for (const row of section.rows) {
      lines.push(toCsvRow(row));
    }
  });

  return lines.join('\r\n');
}

/** Turn arbitrary text into a safe, readable filename fragment. */
export function slugifyForFilename(input: string, fallback = 'export'): string {
  const slug = input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

export function downloadCsvFile(filename: string, content: string): void {
  // BOM keeps accented characters readable when the file is opened in Excel.
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
